/**
 * **plain テーマの HUD**（Issue #523）。**文字だけ**。
 *
 * 2 つ目の HUD として、`core/hud` の契約が本当に実装を分離できているかの**検算**を兼ねる。
 * arcade（格ゲー風）と違うのは:
 *
 * - **枠を持たない**（`plate` を答えない → `core/hud` が `null` を入れる）
 * - 絵を持たない（SVG も画像も無い。文字と細い線だけ）
 * - 盤の写り（`view`）を見ない（舞台の四隅からの余白だけで決まる）
 *
 * それでも**呼ぶ側は同じ名前で場所を引ける**（`life` / `hand` / `turnBadge`）。
 * 文字の下限（`fontPx`）だけは全テーマ共通の決まりなので守る。
 */
import { withDefaults } from '../../core/theme';
import { INK_CLASS, strokeCss } from '../../core/decor';
import { fontPx, partStyle } from '../../core/hud';
import './plain.css';

/**
 * **触れるもの**（Issue #523）。arcade と同じ仕組みが**2 つ目のテーマでも成り立つ**ことの検算。
 * 宣言が少ないテーマは GUI のノブも少ないだけで、扱いは変わらない。
 */
export const HUD_SCHEMA = Object.freeze({
  pad: { type: 'number', label: '四隅からの余白', min: 0, max: 200, step: 1, part: 'hud:plate' },
  columnMax: { type: 'number', label: '列の幅の上限', min: 200, max: 1200, step: 10, part: 'hud:plate' },
  columnRatio: { type: 'number', label: '列の幅（舞台比）', min: 0.1, max: 0.6, step: 0.01, part: 'hud:plate' },
  nameFont: { type: 'number', label: '名前の大きさ', min: 20, max: 140, step: 1, part: 'hud:plate' },
  lifeFont: { type: 'number', label: 'LP の大きさ', min: 20, max: 200, step: 1, part: 'hud:life' },
  handFont: { type: 'number', label: '手札の大きさ', min: 20, max: 140, step: 1, part: 'hud:hand' },
  turnFont: { type: 'number', label: 'ターンの大きさ', min: 20, max: 140, step: 1, part: 'hud:turnBadge' },
  showHand: { type: 'boolean', label: '手札を出す', part: 'hud:hand' },
});

/** 既定値（**ここが唯一の真実**） */
export const HUD_STYLE = Object.freeze({
  pad: 28, columnMax: 560, columnRatio: 0.3,
  nameFont: 48, lifeFont: 76, handFont: 52, turnFont: 56,
  showHand: true,
  // 飾り（`core/decor`）。枠も板も持たないので、**盤の上でも読めるのは縁取りのおかげ**
  'dc/hud:plate/strokeW': 7,
  'dc/hud:life/strokeW': 7,
  'dc/hud:hand/strokeW': 7,
  'dc/hud:turnBadge/strokeW': 7,
});

/** 縁取りを名乗る部品（宣言は `core/decor` が生やす） */
export const HUD_PARTS = Object.freeze(
  ['hud:plate', 'hud:life', 'hud:hand', 'hud:turnBadge'].map((part) => Object.freeze({ part, decor: ['stroke'] })),
);

const hudStyle = withDefaults(() => [HUD_STYLE]);

/** 舞台の四隅からの余白 */
export const PAD = HUD_STYLE.pad;

const NAME_H = 58;
const LIFE_H = 92;
const HAND_H = 62;

/** 1 人ぶんの列の幅（舞台の 3 割まで） */
export const columnWidth = (canvas, style = null) => {
  const v = hudStyle(style);
  return Math.min(v.columnMax, canvas.w * v.columnRatio);
};

/** 文字の大きさ（全部 `fontPx` を通すので下限 46px を下回らない） */
export const FONT = Object.freeze({
  name: fontPx(HUD_STYLE.nameFont), life: fontPx(HUD_STYLE.lifeFont),
  hand: fontPx(HUD_STYLE.handFont), turn: fontPx(HUD_STYLE.turnFont),
});

/** そのフレームの文字の大きさ（`style` を当てたもの） */
export const fontsOf = (style = null) => {
  const v = hudStyle(style);
  return { name: fontPx(v.nameFont), life: fontPx(v.lifeFont), hand: fontPx(v.handFont), turn: fontPx(v.turnFont) };
};

/** ターン・フェイズの 1 行の幅の見積もり（**DOM を測らない**。平均字幅 ≒ 0.6em） */
export const turnLineWidth = (values, font = FONT.turn) => {
  const turn = values?.turn > 0 ? `TURN ${values.turn}`.length : 0;
  const phase = values?.phaseLabel ? values.phaseLabel.length + 3 : 0;
  return Math.round((turn + phase) * font * 0.6) + PAD * 2;
};

export function anchors({ canvas, style }) {
  const v = hudStyle(style);
  const f = fontsOf(style);
  const PAD = v.pad;
  const w = columnWidth(canvas, style);
  const left = (p) => (p === 1 ? canvas.w - PAD - w : PAD);
  const lifeY = PAD + NAME_H;
  return {
    // **`plate` は答えない**（枠が無い HUD なので、core が null を入れる）
    /** LP の行（x, y は左上） */
    life: (p) => ({ x: left(p), y: lifeY, w, h: LIFE_H }),
    /** 手札の行（**x, y は中心**。契約どおり） */
    hand: (p) => ({ x: left(p) + w / 2, y: lifeY + LIFE_H + HAND_H / 2, w, h: HAND_H }),
    /** **そのフレームの値**で幅が決まる（x は中心・y は上端） */
    turnBadge: (vals) => ({
      x: canvas.w / 2,
      y: PAD,
      w: turnLineWidth(vals, f.turn),
      h: Math.round(f.turn * 1.4),
      baseH: Math.round(f.turn * 1.4),
    }),
  };
}

/** 名前の行（`ANCHOR_PORT` に無いものは HUD の中で決めてよい） */
const nameRow = (anchors, p, pad = PAD) => {
  const life = anchors.life(p);
  return { x: life.x, y: pad, w: life.w, h: NAME_H };
};

const box = (r) => ({ left: r.x, top: r.y, width: r.w, height: r.h });
/**
 * `anchors` の箱 → CSS。**`core/hud::partStyle` を通す**ので、共通のトランスフォーム
 * （位置・スケール・回転・不透明度）がこのテーマでもそのまま効く（Issue #523）。
 * x, y が中心の口（`hand` / `turnBadge`）は左上に直してから渡す。
 */
const placed = (r) => partStyle(r);
const centered = (r) => partStyle({ ...r, x: r.x - r.w / 2, y: r.y - r.h / 2 });

/**
 * @param values `core/hud::hudValues` を通した「そのフレームの値」
 * @param anchors `core/hud` が口を埋めた場所（枠が無いので `plate` は null）
 */
export default function PlainHud({ values, anchors, options, style }) {
  const v = hudStyle(style);
  /** 縁取り（`core/decor`）。**CSS に書かない**ので、テーマを詰めるときに太さと色を動かせる */
  const ink = (part) => strokeCss(style, part);
  const FONT2 = fontsOf(style);
  const badge = anchors.turnBadge(values);
  const showHand = v.showHand !== false;
  return (
    <div className="de-hud-plain">
      {[0, 1].map((i) => {
        const right = i === 1;
        const player = values.players[i];
        const active = values.turn > 0 && values.turnPlayer === i;
        const side = `de-plain__row ${INK_CLASS} de-plain__row--${right ? 'right' : 'left'}${active ? ' is-turn' : ''}`;
        return (
          <div key={i} style={{ '--player-color': player.color }} data-testid={`hud-side-${i}`}>
            <div className={`${side} de-plain__name`} style={{ ...box(nameRow(anchors, i, v.pad)), fontSize: FONT2.name, ...ink('hud:plate') }}>
              <span className="de-plain__tag">{i + 1}P</span>
              {' '}
              {player.name || player.kanji || ''}
              {player.deckName ? `（${player.deckName}）` : ''}
            </div>
            <div className={`${side} de-plain__life`} style={{ ...placed(anchors.life(i)), fontSize: FONT2.life, ...ink('hud:life') }} data-testid={`hud-lp-${i}`}>
              {Math.round(values.life[i])}
            </div>
            {showHand ? (
              <div className={`${side} de-plain__hand`} style={{ ...centered(anchors.hand(i)), fontSize: FONT2.hand, ...ink('hud:hand') }} data-testid={`hud-hand-${i}`}>
                {`手札 ${values.hands[i]}`}
              </div>
            ) : null}
          </div>
        );
      })}

      {values.turn > 0 || values.phaseLabel ? (
        <div
          className={`de-plain__turn ${INK_CLASS}`}
          style={{
            ...centered({ ...badge, y: badge.y + badge.h / 2 }),
            fontSize: FONT2.turn,
            ...ink('hud:turnBadge'),
            '--player-color': values.players[values.turnPlayer]?.color,
          }}
          data-testid="hud-turntag"
        >
          {values.turn > 0 ? <span data-testid="hud-turn">{`TURN ${values.turn}`}</span> : null}
          {values.turn > 0 && values.phaseLabel ? <span className="de-plain__sep">/</span> : null}
          {values.phaseLabel ? <span data-testid="hud-phase">{values.phaseLabel}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
